import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Flex, Form, Input, Modal, Select, Space, Switch, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { COURIER_KINDS, CourierKindTag, type CourierKind } from "./CourierKind";

type Courier = { id: string; name: string; kind: CourierKind; aliases: number };

/**
 * Order > Kurir: every courier name stores send (J&T at Shopee is not spelled
 * like J&T at TikTok) mapped to one short list. Waves, pools and handovers use
 * the mapped name; an instant or same-day courier is booked only when packed.
 */
export function Couriers() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const manage = can("orders.manage");
  const [editing, setEditing] = useState<Courier | "new" | null>(null);
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const list = useQuery({ queryKey: ["couriers"], queryFn: () => unwrap(api.GET("/v1/couriers")) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["couriers"] });
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
  };
  const map = useMutation({
    mutationFn: (body: { platform: string; raw: string; courierId: string | null }) => unwrap(api.PUT("/v1/couriers/aliases", { body })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (c: Courier) => unwrap(api.DELETE("/v1/couriers/{courierID}", { params: { path: { courierID: c.id } } })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });

  const couriers = list.data?.couriers ?? [];
  // Names not mapped yet first, then the busiest.
  const aliases = [...(list.data?.aliases ?? [])]
    .filter((a) => !unmappedOnly || !a.courierId)
    .sort((a, b) => Number(!!a.courierId) - Number(!!b.courierId) || b.orders - a.orders);
  const unmapped = (list.data?.aliases ?? []).filter((a) => !a.courierId).length;

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 8 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("courier.title")}
        </Typography.Title>
        {manage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("courier.add")}
          </Button>
        )}
      </Flex>
      <Typography.Paragraph type="secondary">{t("courier.help")}</Typography.Paragraph>
      <Space orientation="vertical" size={16} style={{ width: "100%" }}>
        <Card size="small" title={t("courier.list")}>
          <Table<Courier>
            size="small"
            rowKey="id"
            loading={list.isPending}
            dataSource={couriers}
            pagination={CLIENT_PAGING}
            columns={[
              { title: t("common.name"), dataIndex: "name", render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
              {
                title: t("courier.kind"),
                dataIndex: "kind",
                width: 160,
                render: (k: CourierKind) => (k === "regular" ? t("courier.kind.regular") : <CourierKindTag kind={k} />),
              },
              { title: t("courier.names"), dataIndex: "aliases", width: 120, align: "right" },
              ...(manage
                ? [
                    {
                      title: "",
                      width: 150,
                      render: (_: unknown, c: Courier) => (
                        <Space>
                          <Button size="small" onClick={() => setEditing(c)}>
                            {t("common.edit")}
                          </Button>
                          <Button
                            size="small"
                            danger
                            onClick={() =>
                              modal.confirm({
                                title: t("courier.delete"),
                                content: t("courier.deleteConfirm", { name: c.name, n: c.aliases }),
                                okButtonProps: { danger: true },
                                okText: t("common.delete"),
                                cancelText: t("common.cancel"),
                                onOk: () => remove.mutateAsync(c),
                              })
                            }
                          >
                            {t("common.delete")}
                          </Button>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
        <Card
          size="small"
          title={
            <Space>
              {t("courier.storeNames")}
              {unmapped > 0 && <Tag color="gold">{t("courier.unmapped", { n: unmapped })}</Tag>}
            </Space>
          }
          extra={
            <Space>
              <Typography.Text type="secondary">{t("courier.unmappedOnly")}</Typography.Text>
              <Switch size="small" checked={unmappedOnly} onChange={setUnmappedOnly} />
            </Space>
          }
        >
          <Table
            size="small"
            rowKey={(a) => `${a.platform}|${a.raw}`}
            loading={list.isPending}
            dataSource={aliases}
            pagination={CLIENT_PAGING}
            locale={{ emptyText: t("courier.noNames") }}
            columns={[
              { title: t("courier.platform"), dataIndex: "platform", width: 140, render: (p: string) => t(`platform.${p as "shopee"}`) },
              { title: t("courier.storeName"), dataIndex: "label" },
              { title: t("courier.orders30"), dataIndex: "orders", width: 130, align: "right" },
              {
                title: t("courier.mappedTo"),
                width: 260,
                render: (_, a) => (
                  <Select
                    style={{ width: "100%" }}
                    disabled={!manage}
                    allowClear
                    showSearch={{ optionFilterProp: "label" }}
                    placeholder={t("courier.pick")}
                    status={a.courierId ? undefined : "warning"}
                    value={a.courierId ?? undefined}
                    loading={map.isPending && map.variables?.raw === a.raw && map.variables.platform === a.platform}
                    options={couriers.map((c) => ({ value: c.id, label: c.name }))}
                    onChange={(id?: string) => map.mutate({ platform: a.platform, raw: a.raw, courierId: id ?? null })}
                  />
                ),
              },
            ]}
          />
        </Card>
      </Space>
      {editing && <CourierModal courier={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </>
  );
}

function CourierModal({ courier, onClose, onSaved }: { courier: Courier | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [form] = Form.useForm<{ name: string; kind: CourierKind }>();
  const save = useMutation({
    mutationFn: (body: { name: string; kind: CourierKind }) =>
      courier
        ? unwrap(api.PATCH("/v1/couriers/{courierID}", { params: { path: { courierID: courier.id } }, body }))
        : unwrap(api.POST("/v1/couriers", { body })),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open
      title={courier ? t("courier.edit") : t("courier.add")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ name: courier?.name ?? "", kind: courier?.kind ?? "regular" }}
        onFinish={(v) => save.mutate({ name: v.name.trim(), kind: v.kind })}
      >
        <Form.Item name="name" label={t("common.name")} rules={[{ required: true, whitespace: true, message: t("field.required") }]}>
          <Input maxLength={60} autoFocus />
        </Form.Item>
        <Form.Item name="kind" label={t("courier.kind")} extra={t("courier.kindHelp")}>
          <Select options={COURIER_KINDS.map((k) => ({ value: k, label: t(`courier.kind.${k}`) }))} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
